import { type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { Mail, MessagesSquare, Phone } from 'lucide-react'
import { Logo } from '@/components/layout/Logo'
import { InstagramBrandIcon, WhatsAppBrandIcon } from '@/components/icons/SocialBrandIcons'
import { StoreLocationMap } from '@/store/components/StoreLocationMap'
import { StoreScrollLink } from '@/store/components/StoreScrollLink'
import {
  STORE_EXPLORE_LINKS,
  isStoreExploreLinkActive,
} from '@/store/lib/storeExploreLinks'
import { cn } from '@/lib/utils'

interface StoreFooterProps {
  instagram?: string
  email?: string
  phone?: string
  whatsapp?: string
}

function footerChipClass(active = false, extra?: string) {
  return cn('store-footer-chip focus-ring', active && 'store-footer-chip--active', extra)
}

function buildWhatsAppUrl(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return `https://wa.me/${digits}?text=${encodeURIComponent('Hello Ooudh Kraft, I would like to get in touch.')}`
}

function FooterSectionHead({ title }: { title: string }) {
  return (
    <div className="store-footer-section-head">
      <h4 className="type-subsection-title shrink-0">{title}</h4>
      <span className="store-footer-section-line" aria-hidden />
    </div>
  )
}

function FooterChipLink({
  to,
  active,
  className,
  children,
}: {
  to: string
  active?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <StoreScrollLink
      to={to}
      aria-current={active ? 'page' : undefined}
      className={footerChipClass(active, className)}
    >
      {children}
    </StoreScrollLink>
  )
}

function FooterChipAnchor({
  href,
  className,
  children,
  external,
}: {
  href: string
  className?: string
  children: ReactNode
  external?: boolean
}) {
  return (
    <a
      href={href}
      className={footerChipClass(false, className)}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {children}
    </a>
  )
}

export function StoreFooter({
  instagram,
  email,
  phone,
  whatsapp,
}: StoreFooterProps) {
  const { pathname, search } = useLocation()

  const isHome = pathname === '/'
  const isContact = pathname === '/contact'

  const exploreLinks = STORE_EXPLORE_LINKS.map(({ to, label }) => ({
    to,
    label,
    active: isStoreExploreLinkActive(to, pathname, search),
  }))

  return (
    <footer className="brand-footer mt-20">
      <div className="store-container store-footer-grid grid grid-cols-1 gap-8 md:grid-cols-4 md:gap-10">
        <div className="flex w-full flex-col items-center text-center md:col-span-2 lg:items-start lg:text-left">
          <div className="store-footer-logo-wrap">
            <Logo
              variant="store"
              size="lg"
              showText={false}
              linkTo="/"
              markClassName="store-footer-logo bg-transparent !h-44 max-w-[min(100%,22rem)] sm:!h-48 sm:max-w-[min(100%,24rem)] lg:!h-56 lg:max-w-[min(100%,28rem)]"
            />
          </div>
        </div>

        <div className="store-footer-panel">
          <FooterSectionHead title="Explore" />
          <div className="store-footer-chips">
            {exploreLinks.map(({ to, label, active }) => (
              <FooterChipLink key={to} to={to} active={active}>
                {label}
              </FooterChipLink>
            ))}
          </div>
        </div>

        <div className={cn('store-footer-panel', isHome && 'md:col-span-4')}>
          <FooterSectionHead title="Contact" />
          <div className="store-footer-contact-grid">
            <FooterChipLink
              to="/contact"
              active={isContact}
              className="store-footer-contact-chip"
            >
              <MessagesSquare className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Contact form
            </FooterChipLink>
            {email && (
              <FooterChipAnchor
                href={`mailto:${email}`}
                className="store-footer-contact-chip"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Email
              </FooterChipAnchor>
            )}
            {phone && (
              <FooterChipAnchor
                href={`tel:${phone.replace(/\s/g, '')}`}
                className="store-footer-contact-chip"
              >
                <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Call
              </FooterChipAnchor>
            )}
            {whatsapp && (
              <FooterChipAnchor
                href={buildWhatsAppUrl(whatsapp)}
                external
                className="store-footer-contact-chip store-footer-chip--whatsapp"
              >
                <WhatsAppBrandIcon className="h-3.5 w-3.5 shrink-0" />
                WhatsApp
              </FooterChipAnchor>
            )}
            {instagram && (
              <FooterChipAnchor
                href={instagram}
                external
                className="store-footer-contact-chip store-footer-chip--instagram"
              >
                <InstagramBrandIcon className="h-3.5 w-3.5 shrink-0" />
                Instagram
              </FooterChipAnchor>
            )}
          </div>
          {isHome && (
            <StoreLocationMap className="mt-7 max-w-none border-t border-gold-500/15 pt-7" />
          )}
        </div>
      </div>
      <div
        id="store-footer-credit"
        className="space-y-3 border-t border-luxury-800 py-6 text-center"
      >
        <p className="text-xs text-zinc-600">
          © {new Date().getFullYear()} Ooudh Kraft. All rights reserved.
        </p>
        <p className="inline-flex flex-wrap items-center justify-center gap-1.5 text-xs text-zinc-500">
          <span className="text-[10px] uppercase tracking-wide text-zinc-600">
            Designed &amp; Developed with
          </span>
          <span className="text-rose-400/90" aria-hidden>
            ❤️
          </span>
          <span className="text-zinc-600">by</span>
          <a
            href="https://www.nazarnadeem.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium gold-gradient-text underline-offset-4 transition-opacity hover:opacity-80 hover:underline"
          >
            Nazar Nadeem
          </a>
        </p>
      </div>
    </footer>
  )
}
